import {
    IconBell,
    IconBrandApple,
    IconBrandGooglePlay,
    IconPlayerPlay,
    IconQrcode,
    IconUsersGroup,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Card, Text } from '@fragments';
import { useDownloadApp } from '@features/downloadApp';

import appMockup from '@assets/mockups/app-effect.png';

import './styles.scss';

// `label` is a translation key, resolved at render.
const PERKS = [
    { Icon: IconPlayerPlay, label: 'home.download.perks.classes' },
    { Icon: IconUsersGroup, label: 'home.download.perks.feed' },
    { Icon: IconBell, label: 'home.download.perks.notifications' },
];

/**
 * DownloadCard — the mobile-app invite. The app mockup is the card's whole
 * background and the card takes the image's own aspect ratio, so nothing is
 * cropped; a surface-toned scrim rises from the bottom edge and carries the
 * title, three perk captions and the call to action. Grayscale throughout —
 * the only accent is the inverse buttons.
 *
 * On a phone the two store buttons open the device's store directly; on a
 * computer one button opens the QR modal (see features/downloadApp).
 */
export default function DownloadCard() {
    const { t } = useTranslation('dashboard');
    const { isPhone, open, openStore } = useDownloadApp();

    return (
        <Card flat noBodyPadding className="download-card">
            <img
                className="download-card__mockup"
                src={appMockup}
                alt=""
                width={488}
                height={660}
                loading="lazy"
                decoding="async"
            />

            <div className="download-card__content">
                <div className="download-card__copy">
                    <Text.h1 weight={500} contrast={10}>{t('home.download.title')}</Text.h1>
                    <Text.span contrast={6}>{t('home.download.subtitle')}</Text.span>
                </div>

                <ul className="download-card__perks">
                    {PERKS.map(({ Icon, label }) => (
                        <li key={label}>
                            <Text.caption contrast={7} left={<Icon />}>{t(label)}</Text.caption>
                        </li>
                    ))}
                </ul>

                <div className={`download-card__stores${isPhone ? '' : ' download-card__stores--single'}`}>
                    {isPhone ? (
                        <>
                            <Button
                                color="inverse"
                                size="medium"
                                fullWidth
                                startIcon={<IconBrandApple />}
                                onClick={() => openStore('ios')}
                                preventLoading
                            >
                                App Store
                            </Button>
                            <Button
                                color="inverse"
                                size="medium"
                                fullWidth
                                startIcon={<IconBrandGooglePlay />}
                                onClick={() => openStore('android')}
                                preventLoading
                            >
                                Google Play
                            </Button>
                        </>
                    ) : (
                        <Button
                            color="inverse"
                            size="medium"
                            fullWidth
                            startIcon={<IconQrcode />}
                            onClick={open}
                            preventLoading
                        >
                            {t('home.download.cta')}
                        </Button>
                    )}
                </div>
            </div>
        </Card>
    );
}
